import React from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { prop, theme } from 'styled-tools'
import { compose, lifecycle } from 'recompose'
import { Link } from 'react-router-dom'
import { withAuth } from '../auth'

import features from '../features'
import { Overlay } from '../overlay'

import Textarea from '../customer-form/textarea'
import Checkbox from '../customer-form/checkbox'
import Button from '../button/baseButton'
import ArrowLeftIcon from '../icons/arrow-left'
import ArrowRightIcon from '../icons/arrow-right'

import { storeType, valuesType } from '../../../types'
import { validateAddress, validateName } from '../../../utils/validation'
import tracker, { trackFormError } from '../../../utils/tracker'
import { findServiceData } from '../../containers/bada/content'
import Loader from '../loader/Loader'
import { bookingForm, formHandlers } from './bookingHandler'
import InputField from './InputField'
import TermsOverlay from './TermsOverlay'
import Error from '../error/Error'
import { getTranslator } from '../../../utils/translation'
import * as badaAppointmentApi from '../../../utils/badaAppointmentApi'
import LocationIcon from '../icons/location'
import { promiseRetry } from '../../../utils/promiseRetry'
import { withGoogleReCaptcha, RecaptchaMessage } from '../recaptcha'
import { Verification } from './Verification'
import { FormExtraInformation } from './FormInformation'
import AccreditationImages from './AccreditationImages'
import { isCustomer } from '../../../utils/initValues'
import QuickQuestions from '../quick-questions/QuickQuestions'

const CustomerForm = styled.form`
  font-family: ${theme('typography.paragraph.fontFamily')};
`

const StyledInputField = styled(InputField)`
  font-size: ${theme('typography.paragraph.fontSize') || '16px'};
  height: ${theme('forms.fields.height')};
  border: 1px solid ${theme('forms.colors.border')};
  border-radius: ${theme('forms.borderRadius')};
`
const AddressInputField = styled(StyledInputField)`
  text-overflow: ellipsis;
`

const StyledTextarea = styled(Textarea)`
  textarea& {
    border: 1px solid ${theme('forms.colors.border')};

    &:focus,
    &:active {
      outline: none;
      border: 1px solid ${prop('theme.forms.colors.activeBorder')};
      box-shadow: ${theme('colors.accentShadow')};
    }
    &::placeholder {
      color: ${theme('forms.colors.placeholder')};
    }
  }

  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: '16px';

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 60px
      ${prop('theme.forms.colors.prefilledBackground')} inset !important;
  }
`

const StyledLabel = styled.label`
  display: block;
  color: ${theme('colors.darkGrey')};
  font-size: ${theme('forms.label.fontSize')};
  font-weight: ${theme('forms.label.fontWeight')};
  line-height: ${theme('forms.label.lineHeight')};
  margin-bottom: ${theme('forms.label.marginBottom')};
`

const OptinText = styled.span`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('forms.optinText.fontSize')};
  font-weight: ${theme('forms.optinText.fontWeight')};
  line-height: 22px;
  color: ${theme('colors.darkGrey')};
`

const TermsConditions = styled.span`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('forms.termsConditions.fontSize')};
  font-weight: ${theme('forms.termsConditions.fontWeight')};
  font-weight: ${theme('forms.termsConditions.fontWeight')};
  color: ${theme('forms.termsConditions.color')};
  font-weight: ${theme('forms.termsConditions.lineHeight')};
`

const TermsConditionsLink = styled.span`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('forms.termsConditions.fontSize')};
  font-weight: ${theme('forms.termsConditions.fontWeight')};
  font-weight: ${theme('forms.termsConditions.fontWeight')};
  color: ${theme('forms.termsConditions.color')};
  font-weight: ${theme('forms.termsConditions.lineHeight')};
  border-bottom: ${theme('forms.label.link.borderBottom')};
  border-bottom-color: ${theme('forms.label.link.borderBottomColor')};
  padding-bottom: ${theme('forms.label.link.paddingBottom')};
  cursor: pointer;
`

const StyledButton = styled(Button)`
  margin-top: 20px;
  margin-top: ${prop('theme.button.marginTop')};
  font-size: ${theme('button.fontSize', '12px')};
  font-weight: ${theme('button.fontWeight')};
  border: ${theme('button.border')};
  text-transform: ${theme('button.textTransform')};
  min-height: ${theme('button.height')};
  background-color: ${theme('button.backgroundColor')};

  border-radius: ${prop('theme.button.borderRadius')};
  line-height: ${prop('theme.button.lineHeight')};
  letter-spacing: ${prop('theme.button.letterSpacing')};
  padding: ${theme('forms.button.padding')};
  display: flex;
  justify-content: center;
  align-items: center;

  ${(props) => props.theme.media.min.sm`
    width: ${theme('button.blockButtonWidth')};
  `}

  &:not([disabled]):hover {
    ${css(theme('buttonHover'))}
    &:before {
      ${css(theme('buttonHoverBefore'))}
    }
  }

  ${(props) =>
    props.incomplete &&
    css`
      opacity: 0.5;
    `}
`

const StyledCheckboxContainer = styled.div`
  input[type='checkbox']:checked + div {
    border-color: ${theme('forms.colors.checkboxBorder')};
    svg {
      color: ${theme('forms.colors.svgIconColor')};
    }
  }
`
const StyledCheckBox = styled(Checkbox)`
  > div {
    border-color: ${theme('forms.colors.checkboxBorder')};
  }
`

const SplitRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  ${(p) => p.theme.media.min.md`
    flex-direction: row;
  `}
`

const Row = styled.div`
  flex-basis: 100%;
  ${(p) => p.theme.media.min.md`
    flex-basis: 48%;
  `}
`

const AddressInputWrapper = styled.div`
  position: relative;
`

/* eslint-disable prefer-template */
const AddressSearchResult = styled.ul`
  font-size: ${theme('typography.paragraph.fontSize') || '16px'};
  position: absolute;
  z-index: 6;
  background-color: #fff;
  padding: 0;
  margin: 0;
  margin-top: ${(p) => '-' + p.theme.forms.marginBottom};
  width: 100%;
  list-style-type: none;
  border-style: solid;
  border-width: 1px;
  border-color: ${(p) => p.theme.forms.colors.border};
  max-height: 400px;
  overflow-y: scroll;
  top: 65px;
`

/* eslint-enable prefer-template */

const AddressSearchResultItem = styled.li`
  cursor: pointer;
  padding: 7px;
  border-width: 1px;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0);
  display: flex;
  justify-content: space-between;

  &:hover {
    border-color: ${(p) => p.theme.forms.colors.border};
  }

  :nth-child(even) {
    background-color: ${(p) => p.theme.colors.secondaryBackgroundColor};
  }

  svg {
    display: flex;
    align-self: center;
  }
`

const StyledLocationWidthIcon = styled.div`
  display: flex;

  svg {
    margin-right: 7px;
    display: flex;
    align-self: flex-start;
  }
`

const Information = styled.div`
  margin-top: 16px;
  background-color: ${theme('colors.toneA')};
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 12px;
`
const InformationHeading = styled.div`
  font-weight: 700;
`
const InformationText = styled.div``
const InformationTextStrong = styled.div`
  font-weight: 500;
`
const InformationLink = styled(Link)`
  color: inherit;
`
const InformationExternalLink = styled.a`
  color: inherit;
`

const ADDRESS_RESULT_COUNT = 50

const getAddressQuery = (addressData) => {
  if (!addressData || typeof addressData !== 'object') return ''

  const { line1, line2, line3, company, city, postalCode } = addressData

  const postfix = `${city}, ${postalCode}`

  const prefix = [line1, line2, line3].filter((x) => !!x).join(', ')

  if (prefix) return `${prefix}, ${postfix}`

  if (company) {
    const result = `${company}, ${postfix}`
    return result
  }

  return postfix
}

// need to reset assigned address
// in order to force validation
const resetAddress = (values) => {
  if (!values && typeof values === 'object') return

  /* eslint-disable no-param-reassign */
  values.address = ''
  values.address2 = ''
  values.address3 = ''
  values.city = ''
  values.zipcode = ''
  /* eslint-enable no-param-reassign */
}

class Form extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      overlayOpen: false,
      valid: null,
      showVerification: false,
      addressQuery: this.getPreselectedAddress(),
      addressQueryTimeout: null,
      addressResult: null,
      addressDrillDown: false,
      addressError: '',
    }
    this.nameInputRef = React.createRef()
    this.validName = validateName(this.props.values.name)
    this.validAddress = validateAddress(this.props.values.address)
    this.trackInput = this.trackInput.bind(this)
    this.trackSubmit = this.trackSubmit.bind(this)
    this.closeOverlay = this.closeOverlay.bind(this)
    this.openOverlay = this.openOverlay.bind(this)
    this.onVerificationConfirmed = this.onVerificationConfirmed.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleCloseVerification = this.handleCloseVerification.bind(this)
    this.getPreselectedAddress = this.getPreselectedAddress.bind(this)
  }

  componentDidMount() {
    setTimeout(
      () => this.nameInputRef.current && this.nameInputRef.current.focus(),
      1200
    )
    this.props.updateUiState({ customerForm: { touched: false } })
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.errors.verificationCode === 'verification-required' &&
      this.props.submitCount > 0 &&
      !prevProps.errors.verificationCode &&
      !this.state.showVerification
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ showVerification: true })
    }
  }

  getPreselectedAddress() {
    const address = [
      this.props.values.address,
      this.props.values.address2,
      this.props.values.address3,
      this.props.values.city,
      this.props.values.zipcode,
    ]
      .filter(Boolean)
      .join(', ')

    return address
  }

  closeOverlay() {
    this.setState({ overlayOpen: false })
  }

  openOverlay(event) {
    event.preventDefault()
    this.setState({ overlayOpen: true })
  }

  trackInput(event) {
    if (event && event.target && event.target.value) {
      tracker.trackFilled('BADA 2.0 dataform', event.target.name, {
        flow: this.props.flow,
      })
      this.props.updateUiState({ customerForm: { touched: true } })
    }

    const key = event.target.name

    this.props.setFieldTouched(key)
    this.props.validateForm().then(() => {
      const error = this.props.errors[key]
      if (error) {
        trackFormError(key, error, this.props.values[key])
      }
    })
  }

  trackSubmit(id, verified, required) {
    setTimeout(() => {
      const eventCategory =
        this.props.flow === 'followup'
          ? 'BADA 2.0 followup form'
          : 'BADA 2.0 submit form'
      if (
        Object.keys(this.props.errors).length > 0 &&
        !this.props.errors.verificationCode
      ) {
        tracker.trackClickFail(eventCategory, this.props.selectedStore.name)
      } else if (Object.keys(this.props.errors).length === 0) {
        let requireOTP = ''
        if (verified) {
          requireOTP = 'require'
        } else if (required) {
          requireOTP = 'control'
        } else {
          requireOTP = 'not-require'
        }
        const flow = this.props.flow
        const extra = id ? { verificationId: id, requireOTP, flow } : {}
        tracker.trackClickSuccess(
          eventCategory,
          this.props.selectedStore.name,
          extra
        )
        tracker.trackClick(
          'BADA 2.0 newsletter',
          this.props.values.newsletterConsent ? 'true' : 'false',
          { flow }
        )
      }
    }, 100)
  }

  checkError(id) {
    if (this.props.errors[id] && this.props.touched[id]) {
      return this.errorMessages[this.props.errors[id]]
    }
    return ''
  }

  checkValid(id) {
    if (this.props.touched[id]) {
      if (id === 'comments' && this.props.flow === 'followup') {
        return this.props.values.comments.length > 0
      }
      return typeof this.props.errors[id] === 'undefined'
    }
    return undefined
  }

  checkRequiredCheckbox(id) {
    if (this.props.touched[id]) {
      return typeof this.props.errors[id] === 'undefined'
    }
    return undefined
  }

  onVerificationConfirmed(token, id, verified, required) {
    this.props.setFieldValue('verificationToken', token)

    setTimeout(() => {
      this.trackSubmit(id, verified, required)
      this.props.submitForm()
      this.handleCloseVerification()
    }, 50)
  }

  searchAddress(query, refinementId) {
    this.setState({ addressQuery: query, addressDrillDown: !!refinementId })
    clearTimeout(this.state.addressQueryTimeout)
    if (!query || !query.trim()) {
      this.setState({ addressResult: null, addressQueryTimeout: null })
      return
    }

    this.setState({
      addressQueryTimeout: setTimeout(() => {
        promiseRetry(
          badaAppointmentApi.searchAddresses({
            searchText: query,
            refinementId,
            limit: ADDRESS_RESULT_COUNT,
          }),
          2
        )
          .then((addresses) => {
            if (this.state.addressQuery) {
              this.setState({ addressResult: addresses, addressError: '' })
            }
          })
          .catch((e) => {
            const message =
              (e && e.message) || 'An error occured when getting addresses'
            // if address is null replace it with address query
            if (!this.props.values.address)
              this.props.values.address = this.state.addressQuery
            this.setState({ addressError: message, addressResult: null })
          })
          .finally(() => this.setState({ addressQueryTimeout: null }))
      }, 500),
    })
  }

  emitFormikValue(name, value) {
    this.props.handleTextChange({
      target: { name, value },
    })
  }

  selectAddress(address) {
    if (address.type !== badaAppointmentApi.ADDRESS_ITEM_TYPE) {
      this.searchAddress(this.state.addressQuery, address.id)

      return
    }

    promiseRetry(badaAppointmentApi.retrieveAddress(address.id), 2)
      .then((addressData) => {
        if (!addressData.line1 && !addressData.line2 && !addressData.line2) {
          // this is used for companies that dont have any address lines
          this.emitFormikValue(
            'address',
            addressData.company || addressData.city
          )
        } else {
          this.emitFormikValue('address', addressData.line1)
          this.emitFormikValue('address2', addressData.line2)
          this.emitFormikValue('address3', addressData.line3)
        }

        this.emitFormikValue('zipcode', addressData.postalCode)
        this.emitFormikValue('city', addressData.city)
        this.emitFormikValue('country', addressData.countryIso2)

        this.setState({
          addressQuery: getAddressQuery(addressData),
          addressResult: null,
          addressError: '',
        })
      })
      .catch((e) => {
        const message =
          (e && e.message) || 'An error occured when selecting the addresses'
        // if address is null replace it with address query
        if (!this.props.values.address)
          this.props.values.address = this.state.addressQuery
        this.setState({ addressError: message, addressResult: '' })
      })
  }

  handleSubmit(e) {
    if (features.verification) {
      e.preventDefault()
      this.props.validateForm().then((errors) => {
        this.setState({
          showVerification: errors.verificationCode === 'verification-required',
        })
      })
    }
    Object.entries(this.props.errors).forEach(([key, value]) => {
      if (key === 'verificationCode') {
        return
      }
      if (key === 'questions') {
        value.forEach((v) => {
          if (!value || !Array.isArray(value)) {
            return
          }
          trackFormError(`questions_${v}`, 'required', '')
        })
      } else {
        trackFormError(key, value, this.props.values[key])
      }
    })
    this.props.handleSubmit(e)
  }

  handleCloseVerification() {
    this.setState({ showVerification: false })
  }

  render() {
    const {
      values,
      handleTextChange,
      handleCheckboxChange,
      isSubmitting,
      pageContent,
      services,
      errors,
      selectedService,
      selectedStore,
      intl,
      flow,
      preselectedAddress,
      preselectedPhone,
      submitCount,
      previousQuestions,
    } = this.props

    const followUpMeeting = flow === 'followup'
    const serviceData = findServiceData(services, selectedService)

    const t = getTranslator(intl, 'app.form')
    const generalText = getTranslator(intl)

    const questions = serviceData.questions.map((question) => {
      if (submitCount === 0 || !question.required) {
        return question
      }

      const hasError = errors.questions?.includes(question.id) || false
      return {
        ...question,
        notAnswered: hasError,
      }
    })

    const showAddressCmsSetting = serviceData && serviceData.showAddressField
    const showAddressInputs =
      (showAddressCmsSetting && !features.requireCustomerAddress) ||
      (features.requireCustomerAddress && this.state.addressError)

    return (
      <CustomerForm
        onSubmit={this.handleSubmit}
        data-cy="CustomerForm"
        noValidate
        onKeyPress={(event) => {
          if (event.key === 'Enter' && event.target.type !== 'textarea')
            event.preventDefault()
        }}
      >
        {serviceData.formRequiredFields &&
          serviceData.formRequiredFields.length > 0 && (
            <div>* {serviceData.formRequiredFields}</div>
          )}
        {!serviceData.formRequiredFields &&
          pageContent.formRequiredFields &&
          pageContent.formRequiredFields.length > 0 && (
            <div>* {pageContent.formRequiredFields}</div>
          )}
        <br />
        {!serviceData.hideNameField && (
          <>
            <StyledLabel htmlFor="name">
              {serviceData.formName || pageContent.formName}
            </StyledLabel>
            <StyledInputField
              disabled={followUpMeeting}
              id="name"
              name="name"
              type="text"
              value={values.name}
              onChange={handleTextChange}
              onBlur={this.trackInput}
              valid={this.checkValid('name')}
              error={t('name.error')}
              placeholder={t('name.placeholder')}
              inputRef={this.nameInputRef}
            />
          </>
        )}
        <StyledLabel htmlFor="email">
          {serviceData.formEmail || pageContent.formEmail}
        </StyledLabel>
        <StyledInputField
          id="email"
          name="email"
          type="email"
          value={values.email}
          onChange={handleTextChange}
          onBlur={this.trackInput}
          valid={this.checkValid('email')}
          error={t('email.error')}
          placeholder={t('email.placeholder')}
          disabled={
            ((features.useLoggedInCustomerData && isCustomer) ||
              followUpMeeting) &&
            !errors.email
          }
        />
        <StyledLabel htmlFor="phone">
          {serviceData.formPhone || pageContent.formPhone}
        </StyledLabel>
        <StyledInputField
          id="phone"
          name="phone"
          type="tel"
          value={values.phone}
          onChange={handleTextChange}
          onBlur={this.trackInput}
          valid={this.checkValid('phone')}
          placeholder={t('phone.placeholder')}
          error={
            errors.phone === 'required' ? t('phone.missing') : t('phone.error')
          }
          disabled={followUpMeeting && preselectedPhone}
        />
        {this.state.addressError && (
          <Error
            header={generalText('app.error.title')}
            message={
              generalText('app.form.address-search.error-message') ||
              this.state.addressError
            }
          />
        )}
        {features.requireCustomerAddress && !this.state.addressError && (
          <>
            <StyledLabel>
              {serviceData.formAddress ||
                generalText('app.form.address-search.label')}
            </StyledLabel>
            <AddressInputWrapper>
              <AddressInputField
                autocomplete="address"
                id="address"
                data-name="address"
                value={this.state.addressQuery}
                onFocus={(e) => this.searchAddress(e.target.value)}
                onChange={(e) => {
                  resetAddress(values)
                  errors.address = 'Select an address'
                  this.searchAddress(e.target.value)
                }}
                onBlur={this.trackInput}
                valid={this.checkValid('address')}
                placeholder={generalText('app.form.address-search.placeholder')}
                error={t('address.error')}
                disabled={followUpMeeting && preselectedAddress}
              />
              {this.state.addressQueryTimeout && (
                <AddressSearchResult>
                  <li>
                    <Loader size="small" duration={3} />
                  </li>
                </AddressSearchResult>
              )}
              {this.state.addressResult && !this.state.addressQueryTimeout && (
                <AddressSearchResult>
                  {this.state.addressDrillDown && (
                    <AddressSearchResultItem
                      onClick={() =>
                        this.searchAddress(this.state.addressQuery)
                      }
                    >
                      <ArrowLeftIcon width={13} height={13} />
                    </AddressSearchResultItem>
                  )}
                  {this.state.addressResult.map((address) => (
                    <AddressSearchResultItem
                      key={address.id}
                      onClick={() => this.selectAddress(address)}
                    >
                      {address.type === badaAppointmentApi.ADDRESS_ITEM_TYPE ? (
                        <StyledLocationWidthIcon>
                          <LocationIcon width={20} height={20} /> {address.text}
                          , {address.description}
                        </StyledLocationWidthIcon>
                      ) : (
                        <>
                          {address.text}
                          <ArrowRightIcon width={13} height={13} />
                        </>
                      )}
                    </AddressSearchResultItem>
                  ))}
                </AddressSearchResult>
              )}
            </AddressInputWrapper>
          </>
        )}
        {showAddressInputs && (
          <>
            <StyledLabel htmlFor="address">
              {serviceData.formAddress || pageContent.formAddress}
            </StyledLabel>
            <StyledInputField
              id="address"
              name="address"
              data-name="address"
              type="text"
              value={values.address}
              onChange={handleTextChange}
              onBlur={this.trackInput}
              valid={this.checkValid('address')}
              placeholder={t('address.placeholder')}
              error={t('address.error')}
              disabled={followUpMeeting}
            />
            <SplitRow>
              <Row>
                <StyledLabel htmlFor="zipcode">
                  {t('zipcode.label')}
                </StyledLabel>
                <StyledInputField
                  id="zipcode"
                  name="zipcode"
                  data-name="zipcode"
                  type="text"
                  value={values.zipcode}
                  onChange={handleTextChange}
                  onBlur={this.trackInput}
                  valid={this.checkValid('zipcode')}
                  placeholder={t('zipcode.placeholder')}
                  error={t('zipcode.error')}
                  disabled={followUpMeeting}
                />
              </Row>
              <Row>
                <StyledLabel htmlFor="city">{t('city.label', '')}</StyledLabel>
                <StyledInputField
                  id="city"
                  name="city"
                  data-name="city"
                  type="text"
                  value={values.city}
                  onChange={handleTextChange}
                  onBlur={this.trackInput}
                  valid={this.checkValid('city')}
                  placeholder={t('city.placeholder', '')}
                  error={t('city.error')}
                  disabled={followUpMeeting}
                />
              </Row>
            </SplitRow>
          </>
        )}

        {serviceData && serviceData.showTextareaField && (
          <>
            <StyledLabel htmlFor="comments">
              {serviceData.formTextarea || pageContent.formTextarea}
            </StyledLabel>
            <StyledTextarea
              id="comments"
              name="comments"
              placeholder={
                serviceData.formTextareaPlaceholder ||
                pageContent.formTextareaPlaceholder
              }
              onBlur={this.trackInput}
              value={values.comments}
              onChange={handleTextChange}
              error={t('comments.error')}
              valid={this.checkValid('comments')}
            />
          </>
        )}
        {serviceData.questions.length > 0 && serviceData.usePrepform && (
          <QuickQuestions
            questions={questions}
            updateUiState={this.props.updateUiState}
            prepData={{ questions: values.questions || [] }}
            onChange={(data) => {
              const changedQuestion = data.questions.find((nq) => {
                const prevQ = values.questions.find(
                  (pq) => pq.id === nq.id || pq.question === nq.question
                )
                return (
                  JSON.stringify(prevQ?.answers) !== JSON.stringify(nq.answers)
                )
              })
              if (changedQuestion?.answers?.length > 0) {
                tracker.trackQuestionFilled(
                  changedQuestion.id,
                  changedQuestion.answers[0],
                  {
                    flow: this.props.flow,
                  }
                )
              }

              // on initial load of form the questions array is empty.
              // Use that to fill with previous questions
              let dataQuestions = data.questions
              if (!values.questions.length) {
                dataQuestions = data.questions.map((q) => {
                  const previousAnswers = previousQuestions
                    ?.find((pq) => pq.id === q.id || pq.question === q.question)
                    ?.answers?.filter(Boolean)

                  return {
                    ...q,
                    answers: previousAnswers || q.answers || [],
                  }
                })
              }
              this.props.updateUiState({ customerForm: { touched: true } })
              this.emitFormikValue('questions', dataQuestions)
            }}
            onLoading={() => {}}
            isBookingPage
            useShowHideQuestionFeature={
              !!selectedService?.useMandatoryQuestionOnBadaForm ||
              !!selectedStore?.useMandatoryQuestionOnBadaForm
            }
          />
        )}
        {serviceData.formOptin && serviceData.formOptin.length > 0 && (
          <StyledCheckboxContainer>
            <StyledCheckBox
              name="newsletterConsent"
              checked={values.newsletterConsent}
              onChange={handleCheckboxChange}
            >
              <OptinText>{serviceData.formOptin}</OptinText>
            </StyledCheckBox>
          </StyledCheckboxContainer>
        )}
        {!serviceData.formOptin &&
          pageContent.formOptin &&
          pageContent.formOptin.length > 0 && (
            <StyledCheckBox
              name="newsletterConsent"
              checked={values.newsletterConsent}
              onChange={handleCheckboxChange}
            >
              <OptinText>{pageContent.formOptin}</OptinText>
            </StyledCheckBox>
          )}
        {!!features.verification && (
          <Verification
            show={!!errors.verificationCode && this.state.showVerification}
            email={values.email}
            phoneNumber={values.phone}
            storeId={this.props.selectedStore.id}
            storeName={this.props.selectedStore.name}
            meetingType={this.props.selectedService.id}
            onConfirmed={this.onVerificationConfirmed}
            onClose={this.handleCloseVerification}
          />
        )}
        <FormExtraInformation text={serviceData?.formExtraInformation} />
        {!!errors.singleAppointment && !!values.appointmentId && (
          <Information>
            <InformationHeading>You already have a booking</InformationHeading>
            <InformationText>
              You already have a scheduled appointment. if you need to make
              changes, you can{' '}
              <InformationLink to={`/amend/${values.appointmentId}`}>
                reschedule
              </InformationLink>{' '}
              or{' '}
              <InformationLink to={`/cancel/${values.appointmentId}`}>
                cancel
              </InformationLink>{' '}
              your booking at any time.
            </InformationText>
          </Information>
        )}
        {!!errors.recaptcha && (
          <Information>
            <InformationHeading>
              Sorry we can&apos;t complete your booking right now
            </InformationHeading>
            <InformationText>
              Please call your selected store {selectedStore.name} to complete
              your booking
            </InformationText>
            <InformationTextStrong>
              <InformationExternalLink
                href={`tel:${selectedStore.phoneNumber}`}
              >
                {selectedStore.phoneNumber}
              </InformationExternalLink>
            </InformationTextStrong>
          </Information>
        )}
        <StyledButton
          block
          type="submit"
          disabled={isSubmitting || this.state.showVerification}
          onClick={this.trackSubmit}
          size="large"
        >
          {serviceData.formSubmitButtonText || pageContent.formSubmitButtonText}
        </StyledButton>
        <br />
        {features.useRecaptcha && <RecaptchaMessage />}
        {pageContent.showTermsAndConditions && (
          <>
            <TermsConditions>
              {pageContent.termsAndConditionsText}
            </TermsConditions>
            &nbsp;
            <TermsConditionsLink onClick={this.openOverlay}>
              {pageContent.termsAndConditionsLinkLabel}
            </TermsConditionsLink>
          </>
        )}
        {(isSubmitting || this.state.showVerification) && (
          <Loader duration={3} />
        )}
        {pageContent.showTermsAndConditions && (
          <Overlay
            open={this.state.overlayOpen}
            handleChange={this.closeOverlay}
            setClosed={this.closeOverlay}
            render={({ setClosed }) => (
              <TermsOverlay
                setClosed={setClosed}
                termsAndConditionsSummary={
                  pageContent.termsAndConditionsSummary
                }
              />
            )}
          />
        )}
        <AccreditationImages images={pageContent.accreditationImages} />
      </CustomerForm>
    )
  }
}

Form.propTypes = {
  isSubmitting: PropTypes.bool,
  errors: PropTypes.shape(),
  touched: PropTypes.shape(),
  meetingType: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  updateUiState: PropTypes.func.isRequired,
  values: valuesType.isRequired,
  handleTextChange: PropTypes.func.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  selectedStore: storeType,
  selectedService: PropTypes.shape(),
  pageContent: PropTypes.shape(),
}

Form.defaultProps = {
  meetingType: null,
  isSubmitting: false,
  errors: null,
  touched: null,
  pageContent: {},
  selectedStore: null,
  selectedService: null,
  auth: null,
}

const enhance = compose(
  injectIntl,
  withAuth,
  withGoogleReCaptcha,
  bookingForm,
  formHandlers,
  lifecycle({
    componentDidUpdate(prevProps) {
      if (
        this.props.selectedStore.id !== prevProps.selectedStore.id ||
        this.props.selectedService.id !== prevProps.selectedService.id ||
        this.props.selectedService.serviceType !==
          prevProps.selectedService.serviceType
      ) {
        this.props.updateUiState({ customerForm: { touched: false } })
        this.props.resetForm()
      }
      const { isSubmitting, isValidating, errors } = prevProps

      const errorKeys = Object.keys(errors)
      if (errorKeys && errorKeys.length > 0 && isSubmitting && !isValidating) {
        const element = document.getElementById(errorKeys[0])
        if (element) {
          element.focus()
        }
      }
    },
  })
)

export default enhance(Form)
